import { useEffect, useMemo, useState } from "react";
import * as ReactSelect from "react-select";
import Select from "react-select";
import { FaBus } from "react-icons/fa6";
import { coreApiBaseUrl } from "../../store/airport/apis";
import { buildSelectStyles } from "./selectStyles";

const { components } = ReactSelect;

type BusLocation = {
  id: string;
  name: string;
  /** Operator network the city belongs to, e.g. "TAP" or "T40". */
  provider?: string | null;
};

type Props = {
  name: string;
  placeholder: string;
  value: BusLocation | null;
  onChange: (value: BusLocation | null) => void;
  initialSearch?: string;
  kind: "from" | "to";
  dark?: boolean;
  pos?: "left" | "right";
};

const locationsUrl = `${coreApiBaseUrl}/transport/bus/interstate/locations`;

/**
 * City picker for interstate bus search. Uses the same react-select shell and
 * styling as the flight airport picker, so both tabs behave identically — the
 * only difference is the data source (cities from the interstate API, which
 * arrive as one fixed list rather than a per-keystroke lookup).
 */
const BusSearchInput = ({
  name,
  placeholder,
  value,
  onChange,
  initialSearch,
  kind,
  dark = false,
  pos = "left",
}: Props) => {
  const [fromCities, setFromCities] = useState<BusLocation[]>([]);
  const [toCities, setToCities] = useState<BusLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const options = useMemo(
    () => (kind === "from" ? fromCities : toCities),
    [kind, fromCities, toCities],
  );

  useEffect(() => {
    setIsLoading(true);
    fetch(locationsUrl)
      .then((res) => res.json())
      .then((data) => {
        setFromCities(data?.data?.fromCities || []);
        setToCities(data?.data?.toCities || []);
      })
      .catch(() => {
        setFromCities([]);
        setToCities([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  // A city name handed in via the URL (?from=Lagos) is resolved once the
  // list lands, so a shared link opens with the picker already filled.
  const selected = useMemo(() => {
    if (value?.id) return value;

    const initial = (initialSearch || "").trim().toLowerCase();
    if (!initial) return null;

    return options.find((item) => item.name.toLowerCase() === initial) || null;
  }, [value, initialSearch, options]);

  const CustomOption = (props: any) => (
    <components.Option {...props}>
      <div className="font-semibold capitalize cursor-pointer hover:bg-slate-100">
        <div className="flex items-center space-x-4">
          <div className="text-lg text-[#302D63]">
            <FaBus />
          </div>
          <div className="text-sm">
            <div className="p-0 m-0">{props.data.name}</div>
            {props.data.provider && (
              <p className="text-[10px] font-light uppercase p-0 m-0 tracking-wide">
                {props.data.provider}
              </p>
            )}
          </div>
        </div>
      </div>
    </components.Option>
  );

  const CustomNoOptionsMessage = (props: any) => (
    <components.NoOptionsMessage {...props}>
      <span style={{ fontStyle: "italic", color: "#999" }}>
        {isLoading ? "Loading cities…" : "No city matches that search."}
      </span>
    </components.NoOptionsMessage>
  );

  return (
    <div
      className={
        dark
          ? "border-white/25 bg-white/10 outline-none flex flex-col border-[1px] py-1 h-14 rounded-[14px] w-full text-white px-3 focus-within:border-orange-400 transition-colors"
          : "border-[#302D6340] bg-white outline-none flex flex-col border-[1px] py-1 h-14 rounded-[4px] w-full text-[#171717] px-3 focus:border-b-[#EE723A] focus:border-2"
      }
    >
      <label className={dark ? "text-xs !text-white" : "text-xs !text-white"}>
        {name}
      </label>
      <Select
        options={options}
        isLoading={isLoading}
        components={{
          Option: CustomOption,
          DropdownIndicator: () => null,
          NoOptionsMessage: CustomNoOptionsMessage,
        }}
        placeholder={placeholder}
        // Default filtering only looks at the label, so typing an operator
        // ("TAP", "T40") would match nothing despite it being on screen.
        filterOption={(option, raw) => {
          const q = raw.trim().toLowerCase();
          if (!q) return true;
          const city = option.data as BusLocation;
          return (
            city.name.toLowerCase().includes(q) ||
            (city.provider || "").toLowerCase().includes(q)
          );
        }}
        styles={buildSelectStyles({ dark, pos })}
        value={selected}
        onChange={(option) => onChange((option as BusLocation) || null)}
        menuPlacement="auto"
        getOptionLabel={(option: any) => option.name}
        getOptionValue={(option: any) => option.id}
        isClearable
      />
    </div>
  );
};

export default BusSearchInput;
